/**
 * Тело POST /intake/v1/usage. docs/phase-2/03-generation.md §12.1, форма нормативна.
 *
 * Одна строка тела — один вызов модели: батчевую форму `{calls:[…]}` из 01-monitoring
 * §12.4 победила эта (03-generation §12 — нормативный спек метеринга). «Отправка после
 * серии, пачкой» остаётся правилом ПОВЕДЕНИЯ сервиса (ПРАВИЛО 2.6: никакого HTTP между
 * вызовами модели), а не формой тела: пачка — это несколько POST подряд, каждый со своим
 * `Idempotency-Key: usage.<request_id>` — иначе ретрай половины пачки задваивал бы вторую
 * половину.
 *
 * Ни одно значение не считается сервисом: токены берутся из объекта `usage` ответа
 * провайдера как есть; при стриминге — из `finalMessage()`, а не суммой по дельтам.
 * Стоимость здесь не едет вообще: её считает бэкенд по прайс-листу (§12.3, Р8) —
 * сервис, считающий свою цену сам, — это способ пробить потолок NFR-1 молча.
 *
 * `service` в теле дублирует владельца токена намеренно: колонка `llm_call.service`
 * NOT NULL, и бэкенд СВЕРЯЕТ поле с переводом имени владельца (`aiServiceOf`,
 * 05-seams Р-C8.7) — расхождение означает, что отчёт собран не тем, кем подписан.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

export const UsageBodySchema = z.strictObject({
  service: z.enum(['monitor', 'writer']),
  /** null = расход вне бренда (прогрев, служебные вызовы). */
  brand: z.string().regex(BRAND_RE).nullable(),
  /** 'deep' — стадия 2 префильтра, полный текст (01-monitoring §8.3, §8.5); CHECK
   *  llm_call.stage принимает её с миграции 0005. */
  stage: z.enum(['prefilter', 'deep', 'rank', 'generate', 'revise', 'visual', 'other']),
  /** Ключ прайс-листа config/llm-prices.yaml. Модель без цены не метерится (422). */
  model: z.string().max(64),
  /** id ответа Anthropic (msg_…). Он же основа Idempotency-Key и замок ux_llm_call_request. */
  request_id: z.string().max(64),
  tokens: z.strictObject({
    input: z.int().nonnegative(),
    /** Запись кэша по TTL раздельно: 5 минут стоит 1.25×, час — 2× (llm-costs §1). */
    cache_creation_5m: z.int().nonnegative(),
    cache_creation_1h: z.int().nonnegative(),
    cache_read: z.int().nonnegative(),
    output: z.int().nonnegative(),
  }),
  /** Что сервис ОЖИДАЛ от кэша на этом вызове. Основа проверки ПРАВИЛА 2.9. */
  cache_expect: z.enum(['write', 'read', 'none']),
  latency_ms: z.int().nonnegative(),
  monitoring_run_id: Uuid.nullable(),
  draft_version_id: Uuid.nullable(),
  /** Момент вызова модели, не момент отправки отчёта: пачка едет после серии. */
  occurred_at: IsoUtcMs,
});

export type UsageBody = z.output<typeof UsageBodySchema>;
